import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import LogoSpin from '../../../shared/animations/LogoSpin';
import MicrosoftLogo from '../../../assets/logos/others/MicrosoftLogo';
import { useProcessOAuthCallback } from '../hooks/useProcessOAuthCallback';
import './OAuthCallback.css';

const STEPS = [
  { id: 1, label: 'Redirected' },
  { id: 2, label: 'Verifying token' },
  { id: 3, label: 'Starting session' },
  { id: 4, label: 'Complete' },
];

const OAuthCallback = ({ provider = 'oauth', externalError = null, skipProcessing = false }) => {
  const { error: hookError } = useProcessOAuthCallback(provider, skipProcessing);
  // externalError=null means normal flow (hook drives error state)
  // externalError=string means caller (BYOC) drives error state
  const error = externalError !== null ? externalError : hookError;
  const bgCanvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const [activeStep, setActiveStep] = useState(2);

  const providerDisplayName = provider.charAt(0).toUpperCase() + provider.slice(1);

  // Background particle + grid canvas
  useEffect(() => {
    const canvas = bgCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H;

    const particles = Array.from({ length: 45 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.2 + 0.3,
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
      a: Math.random() * 0.35 + 0.08,
    }));

    function resize() {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    function draw() {
      ctx.clearRect(0, 0, W, H);

      // faint grid
      ctx.strokeStyle = 'rgba(148,4,195,0.025)';
      ctx.lineWidth = 0.5;
      const gs = 80;
      for (let x = 0; x < W; x += gs) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
      for (let y = 0; y < H; y += gs) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

      // move + connect particles
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
      });
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < 130) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(148,4,195,${0.07 * (1 - d / 130)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(148,4,195,${p.a})`;
        ctx.fill();
      });

      animFrameRef.current = requestAnimationFrame(draw);
    }
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // Step progression
  useEffect(() => {
    if (error) return;
    const t1 = setTimeout(() => setActiveStep(3), 2500);
    const t2 = setTimeout(() => setActiveStep(4), 4800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [error]);

  // Error state — structure unchanged for test compatibility
  if (error) {
    return (
      <div className="oauth-callback-overlay">
        <div className="oauth-callback-error-card">
          <div className="oauth-callback-error-icon">❌</div>
          <h2 className="oauth-callback-error-title">
            <span className="oauth-callback-provider-name">{providerDisplayName}</span> Authentication Failed
          </h2>
          <p className="oauth-callback-error-message">{error}</p>
          <p className="oauth-callback-error-redirect">Redirecting to login page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="oauth-callback-overlay">
      <canvas ref={bgCanvasRef} className="oauth-bg-canvas" />
      <div className="oauth-glow-center" />

      <div className="oauth-callback-container">

        <div className="oauth-provider-row">
          {provider === 'microsoft' && <MicrosoftLogo fill="#9C9DA7" />}
          <span className="oauth-provider-label">{providerDisplayName}</span>
        </div>

        <LogoSpin size={190} />

        <div className="oauth-headline">
          <h1>
            Verifying your <span className="oauth-accent">identity</span>
            <span className="oauth-dots"><span>.</span><span>.</span><span>.</span></span>
          </h1>
        </div>

        {/* Class names kept verbatim — tests assert on these */}
        

        <div className="oauth-track">
          {STEPS.map((step, i) => {
            const isDone   = step.id < activeStep;
            const isActive = step.id === activeStep;
            const stepClass = isDone ? 'oauth-track-step done' : isActive ? 'oauth-track-step active' : 'oauth-track-step';
            const lineClass = step.id < activeStep ? 'oauth-track-line lit' : 'oauth-track-line';
            return (
              <div key={step.id} style={{ display: 'contents' }}>
                <div className={stepClass}>
                  <div className="oauth-track-dot" />
                  <div className="oauth-track-label">{step.label}</div>
                </div>
                {i < STEPS.length - 1 && <div className={lineClass} />}
              </div>
            );
          })}
        </div>

      </div>

      <div className="oauth-footer">
        <span className="oauth-footer-brand">TONIC</span>
        &nbsp;·&nbsp; OAuth 2.0 &nbsp;·&nbsp; Secured
      </div>
    </div>
  );
};

OAuthCallback.propTypes = {
  provider: PropTypes.oneOf(['microsoft', 'google', 'oauth', 'cognito']).isRequired,
  externalError: PropTypes.string,
  skipProcessing: PropTypes.bool
};

OAuthCallback.defaultProps = {
  provider: 'oauth',
  externalError: null
};

export default OAuthCallback;

export const MicrosoftCallback = () => <OAuthCallback provider="microsoft" />;
export const GoogleCallback    = () => <OAuthCallback provider="google" />;
